import { HISTORY_ACTION_MODE } from '@candulabs/craft';
import { nanoid } from 'nanoid';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'styleguide';

import { TextButton } from '@candu-ai/craft/ui';
import { useToggle } from 'commons/hooks';
import { useNodeConditional } from 'craft/containers/Conditionals';
import { Section, Toggle, ValueBox, ValueGroup } from 'craft/containers/Toolbox/components';
import { useEditor, useNodesData } from 'craft/hooks';

import { ChecklistEventModalConditional } from './ChecklistEventModalConditional';

interface Props {
  nodeIds: string[];
}

const StyledToggle = styled(Toggle)`
  width: 48px;
`;

export const CheckListItemSettings = ({ nodeIds }: Props) => {
  const [nodeData] = useNodesData(nodeIds);
  const { query, actions } = useEditor();
  const [isManualChecklist, setIsManualChecklist] = useState(true);
  const [isConditionalModalOpen, toggleConditionalModal] = useToggle();
  const { conditional, setConditional } = useNodeConditional(nodeIds[0]);

  const setStateProvider = () => {
    const stateId = nanoid(6);
    actions.produceState((state) => {
      // eslint-disable-next-line no-param-reassign
      state.nodes[nodeIds[0]].data.custom.states = { [stateId]: {} };
    }, HISTORY_ACTION_MODE.MERGE);
  };

  if (nodeIds.length !== 1) {
    return null;
  }

  const parentId = nodeData?.parent;
  if (!parentId) {
    return null;
  }

  const parent = query.node(parentId);
  const isUserScope = parent?.node?.data?.props?.scope === 'user';

  if (!isUserScope) {
    return null;
  }

  const isManualChecklistActive = isManualChecklist && !conditional;

  return (
    <Section name="Checklist Item Settings">
      <ValueGroup>
        <ValueBox dark label="Completion on">
          <Tooltip content="Mark as completed on user click" position="top" showDelay={500}>
            <StyledToggle
              toggleGroup="checklist-trigger"
              onClick={() => {
                setConditional(undefined);
                setIsManualChecklist(true);
                setStateProvider();
              }}
              isActive={isManualChecklistActive}
              icon={() => <span>Click</span>}
            />
          </Tooltip>
          <Tooltip
            content="Mark as completed on an user event being triggered"
            position="top"
            showDelay={500}
          >
            <StyledToggle
              toggleGroup="checklist-scope"
              onClick={() => {
                setIsManualChecklist(false);
                toggleConditionalModal();
              }}
              isActive={!isManualChecklistActive}
              icon={() => <span>Event</span>}
            />
          </Tooltip>
        </ValueBox>
        {!isManualChecklistActive && (
          <TextButton onClick={toggleConditionalModal}>See completion criteria</TextButton>
        )}
        {isConditionalModalOpen && (
          <ChecklistEventModalConditional nodeId={nodeIds[0]} onClose={toggleConditionalModal} />
        )}
      </ValueGroup>
    </Section>
  );
};
